import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { SuggestionData, SolutionData } from '@/types/solution';
import { useUserName } from '@/hooks/useUserName';
import { useUserEmail } from '@/hooks/useUserEmail';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, ExternalLink } from "lucide-react";
import { useToast } from '@/hooks/use-toast';
import ReviewSuggestionDialog from './ReviewSuggestionDialog';
import { convertToCanonicalFormat, convertToSolutionsTableFormat } from '@/utils/canonicalStructure';
import { syncTagsToSolution } from '@/services/tagService';

// Utility function for generating SEO-friendly slugs
const generateSlugFromName = (name: string): string => {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
};

const SuggestionsReview = () => {
  const { toast } = useToast();
  const [selectedSuggestion, setSelectedSuggestion] = useState<SuggestionData | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  
  // Get persistent user name and email
  const { userName: reviewerName, setUserName: setReviewerName } = useUserName();
  const { userEmail: reviewerEmail, setUserEmail: setReviewerEmail } = useUserEmail();

  // Fetch all edit suggestions
  const { data: suggestions, isLoading, error, refetch } = useQuery({
    queryKey: ['editSuggestions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('edit_suggestions')
        .select(`
          *,
          solutions:solution_id (
            id,
            name,
            slug
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as (SuggestionData & { solutions: Pick<SolutionData, 'id' | 'name' | 'slug'> })[];
    }
  });

  const openReviewDialog = (suggestion: SuggestionData) => {
    setSelectedSuggestion(suggestion);
    setReviewDialogOpen(true);
  };

  const updateSuggestionStatus = async (
    suggestionId: string, 
    status: 'accepted' | 'rejected', 
    reviewerNotes?: string,
    modifiedSuggestion?: string
  ) => {
    try {
      const suggestionToAccept = selectedSuggestion;
      if (!suggestionToAccept) throw new Error("Missing suggestion data");
      
      // Handle delete requests (to_classify status) differently
      if (suggestionToAccept.status === 'to_classify' && suggestionToAccept.solution_id) {
        // Update the suggestion status to 'classified'
        const { error: suggestionUpdateError } = await supabase
          .from('edit_suggestions')
          .update({ status: 'classified' })
          .eq('id', suggestionId);
          
        if (suggestionUpdateError) throw suggestionUpdateError;
        
        // Mark the solution as classified
        const { error: solutionError } = await supabase
          .from('solutions')
          .update({ status: 'classified', updated_at: new Date().toISOString() })
          .eq('id', suggestionToAccept.solution_id);
          
        if (solutionError) throw solutionError;
        
        toast({
          title: 'Solution classified',
          description: 'The solution has been marked as classified and removed from public view.',
        });
        
        refetch();
        setReviewDialogOpen(false);
        return;
      } else {
        // Get the JSON content to use (either modified or original)
        const jsonContent = modifiedSuggestion || suggestionToAccept.suggestion;
        let parsedContent;
        let canonicalContent;
        
        try {
          parsedContent = JSON.parse(jsonContent);
          // First convert to canonical format
          canonicalContent = convertToCanonicalFormat(parsedContent);
          
          // Extract metadata for database fields
          const name = canonicalContent.metadata?.name || 'Unnamed Solution';
          const shortDescription = canonicalContent.metadata?.shortDescription || 'No description provided';
          const imageUrl = canonicalContent.metadata?.imageUrl || null;
          
          // Extract tags from metadata if present
          const tags = canonicalContent.metadata?.tags || [];
          
          // Convert to solutions table format for details column (removes metadata)
          const solutionsTableFormat = convertToSolutionsTableFormat(canonicalContent);
          
          const updateData = {
            name,
            short_description: shortDescription,
            image_url: imageUrl,
            details: solutionsTableFormat,  // Store without metadata in details column
            updated_at: new Date().toISOString()
          };
          
          // Handle differently based on whether it's a new solution or an edit
          if (suggestionToAccept.status === 'new_solution') {
            // Extract tags before creating solution
            const tags = canonicalContent.metadata?.tags || [];
            
            // Generate slug from solution name
            const slug = generateSlugFromName(name);
            
            // Create the solution first
            const { error: solutionError, data: createdSolution } = await supabase
              .from('solutions')
              .insert({
                ...updateData,
                slug: slug
              })
              .select('id')
              .single();
              
            if (solutionError) throw solutionError;
            
            if (createdSolution) {
              // Sync tags immediately after creating solution
              await syncTagsToSolution(supabase, createdSolution.id, tags);
              
              // Then update the suggestion status
              const { error: suggestionUpdateError } = await supabase
                .from('edit_suggestions')
                .update({
                  solution_id: createdSolution.id,
                  status: status,
                  reviewed: true,
                  reviewed_at: new Date().toISOString(),
                  reviewer_notes: reviewerNotes || null
                })
                .eq('id', suggestionId);
                
              if (suggestionUpdateError) {
                console.error('Error updating suggestion after solution creation:', suggestionUpdateError);
              }
            }
          } else if (suggestionToAccept.solution_id) {
            // Update existing solution
            const { error: solutionError } = await supabase
              .from('solutions')
              .update(updateData)
              .eq('id', suggestionToAccept.solution_id);
              
            if (solutionError) throw solutionError;

            // Sync tags for existing solution
            await syncTagsToSolution(supabase, suggestionToAccept.solution_id, tags);

            // Update suggestion status
            const { error: suggestionUpdateError } = await supabase
              .from('edit_suggestions')
              .update({
                status: status,
                reviewed: true,
                reviewed_at: new Date().toISOString(),
                reviewer_notes: reviewerNotes || null,
                reviewer_name: reviewerName,
                reviewer_email: reviewerEmail
              })
              .eq('id', suggestionId);
              
            if (suggestionUpdateError) {
              console.error('Error updating suggestion status:', suggestionUpdateError);
              throw suggestionUpdateError;
            }
          }
        } catch (err) {
          toast({
            title: 'Invalid JSON',
            description: 'The suggestion could not be parsed as valid JSON.',
            variant: 'destructive',
          });
          return;
        }
      }
      
      toast({
        title: `Suggestion ${status}`,
        description: `The suggestion has been marked as ${status}.`,
      });
      
      refetch();
      setReviewDialogOpen(false);
    } catch (error) {
      console.error('Error updating suggestion:', error);
      toast({
        title: 'Update failed',
        description: 'There was an error updating the suggestion status.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) return <div>Loading suggestions...</div>;
  if (error) return <div>Error loading suggestions: {(error as Error).message}</div>;

  return (
    <div className="space-y-4">
      <div className="mb-4 flex items-start space-x-4">
        <div className="flex-1">
          <label htmlFor="reviewer-name" className="block text-sm font-medium text-gray-700 mb-1">
            Your Name (as Reviewer)
          </label>
          <input
            type="text"
            id="reviewer-name"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your name"
          />
        </div>
        
        <div className="flex-1">
          <label htmlFor="reviewer-email" className="block text-sm font-medium text-gray-700 mb-1">
            Your Email (as Reviewer)
          </label>
          <input
            type="email"
            id="reviewer-email"
            value={reviewerEmail}
            onChange={(e) => setReviewerEmail(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your email"
          />
        </div>
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Solution ID</TableHead>
              <TableHead>Solution Title</TableHead>
              <TableHead>Submitted By</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Review Date</TableHead>
              <TableHead>Reviewer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suggestions && suggestions.length > 0 ? (
              suggestions.map((suggestion) => (
                <TableRow key={suggestion.id}>
                  <TableCell className="font-mono text-xs">
                    {suggestion.solution_id ? suggestion.solution_id.substring(0, 8) + '...' : 'N/A'}
                  </TableCell>
                  <TableCell className="font-medium">
                    {suggestion.status === 'new_solution' ? 
                      (() => {
                        try {
                          const parsed = JSON.parse(suggestion.suggestion);
                          return parsed.metadata?.name || parsed.name || 'Unknown';
                        } catch (e) {
                          console.error('Error parsing suggestion for display:', e);
                          return 'Parse Error';
                        }
                      })() : 
                      suggestion.solutions?.name || 'Unknown'} 
                    {suggestion.solution_id && suggestion.solutions?.slug && (
                      <a 
                        href={`/solution/${suggestion.solutions.slug}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-2 inline-flex items-center text-xs text-blue-500 hover:text-blue-700"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />View
                      </a>
                    )}
                  </TableCell>
                  <TableCell>{suggestion.suggester_name}</TableCell>
                  <TableCell>{suggestion.email}</TableCell>
                  <TableCell>
                    {suggestion.created_at ? format(new Date(suggestion.created_at), 'MMM d, yyyy HH:mm') : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {suggestion.reviewed_at ? format(new Date(suggestion.reviewed_at), 'MMM d, yyyy HH:mm') : 'Not reviewed yet'}
                  </TableCell>
                  <TableCell>
                    {suggestion.reviewer_name || 'Not reviewed yet'}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      suggestion.status === 'accepted' ? 'bg-green-100 text-green-800' : 
                      suggestion.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                      suggestion.status === 'new_solution' ? 'bg-blue-100 text-blue-800' :
                      suggestion.status === 'to_classify' ? 'bg-orange-100 text-orange-800' :
                      suggestion.status === 'classified' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {suggestion.status === 'new_solution' ? 'New Solution' : 
                       suggestion.status === 'to_classify' ? 'Delete Request' :
                       suggestion.status === 'classified' ? 'Classified' :
                       suggestion.status || 'Pending'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openReviewDialog(suggestion)}
                      >
                        Review
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-4">
                  No suggestions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      
      {selectedSuggestion && (
        <ReviewSuggestionDialog
          open={reviewDialogOpen}
          onOpenChange={setReviewDialogOpen}
          suggestion={selectedSuggestion}
          onAccept={(notes, modifiedSuggestion) => updateSuggestionStatus(selectedSuggestion.id, 'accepted', notes, modifiedSuggestion)}
          onReject={(notes) => updateSuggestionStatus(selectedSuggestion.id, 'rejected', notes)}
        />
      )}
    </div>
  );
};

export default SuggestionsReview;
